import { Category } from "../category/Category";
import { OrderDetail, PurchaseOrderPartNumber, RequisitionPartNumber } from "../document/Document";
import { Opportunity } from "../opportunities/Opportunity";
import { SalesStatistics } from "../reseller/Reseller";
import type { Product as ApiProduct, TransactionHistory as ApiTransactionHistory } from "randmar-api-client";
import type { AttachmentHeader } from "randmar-api-client";

export class WarehouseInventory {
  RandmarSKU?: string;
  WarehouseId?: string;
  WarehouseType?: string;
  Name?: string;
  City?: string;
  Province?: string;
  Country?: string;
  AvailableQuantity?: number;
  PurchaseOrderQuantity?: number;
  BinLocation?: string;
}

const LEGACY_WEST_WAREHOUSES = ["VAN", "EDM"];
const LEGACY_EAST_WAREHOUSES = ["MTL", "TOR"];

export class Inventory {
  price?: number;
  regularPrice?: number;
  cost?: number;
  currency?: string;
  Inventory?: WarehouseInventory[];

  private _totalForWarehouses(warehouseIds: string[]): number {
    if (!this.Inventory) return 0;
    return this.Inventory
      .filter((x) => warehouseIds.includes(x.WarehouseId || ""))
      .reduce((a, b) => a + (b.AvailableQuantity || 0), 0);
  }

  public totalWest() {
    return this._totalForWarehouses(LEGACY_WEST_WAREHOUSES);
  }

  public totalEast() {
    return this._totalForWarehouses(LEGACY_EAST_WAREHOUSES);
  }

  public totalCanada() {
    return this.totalEast() + this.totalWest();
  }

  public total() {
    if (!this.Inventory) return 0;
    return this.Inventory.reduce((a, b) => a + (b.AvailableQuantity || 0), 0);
  }
}

export class InstantRebate {
  constructor(
    public randmarSKU: string,
    public rebateAmount: number,
    public startDate: string,
    public endDate: string,
    public promotionId: string) { }

  public static convertFrom(source: unknown): InstantRebate {
    const record = source as Record<string, unknown>;
    return new InstantRebate(
      typeof record.RandmarSKU === "string" ? record.RandmarSKU : "",
      typeof record.RebateAmount === "number" ? record.RebateAmount : 0,
      typeof record.StartDate === "string" ? record.StartDate : "",
      typeof record.EndDate === "string" ? record.EndDate : "",
      typeof record.PromotionId === "string" ? record.PromotionId : ""
    );
  }
}

export class Product {

  public autoUpdate?: boolean;
  public category?: string;
  public createdBy?: string;
  public mpn?: string;
  public manufacturerName?: string;
  public masterCarton?: number;
  public productType?: string;
  public randmarSKU?: string;
  public serialNumber?: boolean;
  public transparencyCode?: boolean;
  public opportunityOnly?: boolean;
  public availableToBuy?: boolean;
  public upc?: string;
  public unitWeight?: number;
  public unitHeight?: number;
  public unitLength?: number;
  public unitWidth?: number;
  public skidQuantity?: number;
  public isManufacturer?: boolean;
  public bodyHTML?: string;
  public title?: string;
  public map?: number;
  public msrp?: number;
  public state?: string;
  public require3DScan?: boolean;
  public voiceoverCaption?: string;
  public sceneName?: string;
  public countryCodeOfOrigin?: string;

  public distribution?: Inventory;
  public requisitionsPartNumbers?: RequisitionPartNumber[];
  public purchaseOrdersPartNumbers?: PurchaseOrderPartNumber[];
  public instantRebates?: InstantRebate[];
  public manufacturerCategory?: Category;
  public opportunities?: Opportunity[];
  public activeOrderDetails?: OrderDetail[];
  public completedOrderDetails?: OrderDetail[];
  public salesStatistics?: SalesStatistics[];
  public attachments?: AttachmentHeader[];
  public source?: ApiProduct;

  public static convertFrom(source: ApiProduct): Product {
    return Product.convertFromNewProduct(source);
  }

  public static convertFromNewProduct(source: ApiProduct): Product {
    const product = new Product();

    product.autoUpdate = source.AutoUpdate === 1;
    product.category = source.Category ?? undefined;
    product.createdBy = source.CreatedBy ?? undefined;
    product.mpn = source.MPN ?? undefined;
    product.manufacturerName = source.ManufacturerName ?? undefined;
    product.masterCarton = source.MasterCarton;
    product.productType = source.ProductType ?? undefined;
    product.randmarSKU = source.RandmarSKU ?? undefined;
    product.serialNumber = source.SerialNumber;
    product.transparencyCode = source.TransparencyCode;
    product.opportunityOnly = source.OpportunityOnly;
    product.availableToBuy = source.AvailableToBuy ?? false;
    product.upc = source.UPC ?? undefined;
    product.unitWeight = source.UnitWeight;
    product.unitHeight = source.UnitHeight ?? undefined;
    product.unitLength = source.UnitLength ?? undefined;
    product.unitWidth = source.UnitWidth ?? undefined;
    product.skidQuantity = source.SkidQuantity ?? undefined;
    product.isManufacturer = source.IsManufacturer ?? false;
    product.bodyHTML = source.BodyHTML ?? undefined;
    product.title = source.Title ?? undefined;
    product.map = source.MAP ?? undefined;
    product.msrp = source.MSRP ?? undefined;
    product.state = source.State ?? undefined;
    product.voiceoverCaption = source.VoiceoverCaption ?? undefined;
    product.sceneName = source.SceneName ?? undefined;
    product.countryCodeOfOrigin = source.CountryCodeOfOrigin ?? undefined;
    product.require3DScan = source.Require3DScan;
    product.source = source;

    if (source.Distribution) {
      product.distribution = new Inventory();
      product.distribution.price = source.Distribution.Price;
      product.distribution.regularPrice = source.Distribution.RegularPrice;
      product.distribution.cost = source.Distribution.Cost ?? undefined;
      product.distribution.currency = source.Distribution.Currency ?? undefined;

      product.distribution.Inventory = source.Distribution.Inventory?.map((x) => {
        return {
          RandmarSKU: x.RandmarSKU ?? undefined,
          WarehouseId: x.WarehouseId ?? undefined,
          WarehouseType: x.WarehouseType ?? undefined,
          Name: x.Name ?? undefined,
          City: x.City ?? undefined,
          Province: x.Province ?? undefined,
          Country: x.Country ?? undefined,
          AvailableQuantity: x.AvailableQuantity,
          PurchaseOrderQuantity: x.PurchaseOrderQuantity,
          BinLocation: x.BinLocation ?? undefined
        };
      });
    }

    if (Array.isArray(source.InstantRebates)) {
      product.instantRebates = source.InstantRebates.map(InstantRebate.convertFrom);
    }

    if (Array.isArray(source.Requisitions)) {
      product.requisitionsPartNumbers = source.Requisitions.map(RequisitionPartNumber.convertFrom);
    }

    if (Array.isArray(source.PurchaseOrders)) {
      product.purchaseOrdersPartNumbers = source.PurchaseOrders.map(PurchaseOrderPartNumber.convertFrom);
    }

    if (Array.isArray(source.Opportunities)) {
      product.opportunities = source.Opportunities.map(Opportunity.convertFrom);
    }

    if (source.ManufacturerCategory) {
      product.manufacturerCategory = Category.convertFrom(source.ManufacturerCategory);
    }

    if (Array.isArray(source.ActiveOrderDetails)) {
      product.activeOrderDetails = source.ActiveOrderDetails.map(OrderDetail.convertFromApi);
    }

    if (Array.isArray(source.CompletedOrderDetails)) {
      product.completedOrderDetails = source.CompletedOrderDetails.map(OrderDetail.convertFromApi);
    }

    if (Array.isArray(source.SalesStatistics)) {
      product.salesStatistics = source.SalesStatistics.map(SalesStatistics.convertFrom);
    }

    if (Array.isArray(source.Attachments)) {
      product.attachments = source.Attachments;
    }

    return product;
  }
}

export class TransactionHistory {
  constructor(
    public documentNumber?: string,
    public category?: string,
    public location?: string,
    public transactionDate?: number,
    public transactionType?: string,
    public quantity?: number,
    public linkedDocument?: string) { }

  public static convertFrom(source: ApiTransactionHistory): TransactionHistory {
    const record = source as unknown as Record<string, unknown>;
    return new TransactionHistory(
      source.DocumentNumber ?? undefined,
      source.Category ?? undefined,
      source.Location ?? undefined,
      source.TransactionDate,
      source.TransactionType ?? undefined,
      source.Quantity,
      typeof record.LinkedDocument === "string" ? record.LinkedDocument : undefined
    );
  }
}
